/**
 * The assembly's welcome page — where a church's own QR code lands.
 *
 * Someone scans the code on the noticeboard outside Newport and arrives here.
 * They should not have to search a world directory to find the church they are
 * standing in front of, and they should not have to read a homepage to work
 * out what to do next. Three things, large enough to tap with a thumb:
 * find out more, plan a visit, become a member.
 *
 * If they happen to be a member of another FWM assembly and are signed in,
 * they are offered a transfer instead of a second membership — scanning a code
 * never moves anybody, it only asks Newport whether they will receive them.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MapPin, Phone, Mail, Globe, Clock, ArrowRight, UserPlus, CalendarHeart,
  Info, Loader2, Church, ArrowRightLeft, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PlanVisitDialog } from '@/components/church/ChurchCtaButtons';
import { captureSource, getSource } from '@/lib/qrSource';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const Action = ({ icon: Icon, title, hint, onClick, to, testId, tone = 'light' }) => {
  const body = (
    <>
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
        tone === 'dark' ? 'bg-white/15 text-white' : 'bg-amber-50 text-amber-700'}`}>
        <Icon className="h-6 w-6" />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className={`block text-base font-semibold ${
          tone === 'dark' ? 'text-white' : 'text-stone-900'}`}>{title}</span>
        <span className={`block text-sm ${
          tone === 'dark' ? 'text-white/70' : 'text-stone-500'}`}>{hint}</span>
      </span>
      <ArrowRight className={`h-5 w-5 shrink-0 ${
        tone === 'dark' ? 'text-white/60' : 'text-stone-400'}`} />
    </>
  );
  const className = `flex min-h-[72px] w-full items-center gap-4 rounded-2xl border p-4 transition-colors ${
    tone === 'dark'
      ? 'border-white/15 bg-stone-900 hover:bg-stone-800'
      : 'border-stone-200 bg-white hover:border-amber-300'}`;
  return to
    ? <Link to={to} className={className} data-testid={testId}>{body}</Link>
    : <button type="button" onClick={onClick} className={className} data-testid={testId}>{body}</button>;
};

export default function ChurchWelcomePage() {
  const { slug } = useParams();
  const [church, setChurch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitOpen, setVisitOpen] = useState(false);
  const [standing, setStanding] = useState(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { captureSource(); }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/find-church/assembly/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setChurch)
      .catch(() => setChurch(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Members arrive through two different doors — the church portal and the
  // Member tab on the main sign-in page — so the session is read the same way
  // the rest of the app reads it, rather than from one hard-coded key.
  useEffect(() => {
    const token = getToken() || localStorage.getItem('member_token');
    if (!token || !church?.id) return;
    fetch(`${API}/api/church-transfers/standing?church_id=${church.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStanding)
      .catch(() => setStanding(null));
  }, [church?.id]);

  const requestTransfer = async () => {
    setRequesting(true);
    try {
      const res = await fetch(`${API}/api/church-transfers/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken() || localStorage.getItem('member_token')}`,
        },
        body: JSON.stringify({
          to_church_id: church.id,
          reason: 'Relocated — found this assembly through its QR code',
          source_code: getSource(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not send your request');
      toast.success(data.message || 'Your transfer request has been sent.');
      setStanding((s) => ({ ...s, action: 'pending', can_request_transfer: false,
                            pending_request: data.request }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-stone-50">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </main>
    );
  }

  if (!church) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6 text-center"
            data-testid="welcome-not-found">
        <Church className="h-10 w-10 text-stone-300" />
        <h1 className="text-xl font-semibold text-stone-900">We could not find that church</h1>
        <p className="max-w-sm text-sm text-stone-500">
          The code may be for an assembly that has since changed. You can still
          find your nearest church.
        </p>
        <Button asChild className="min-h-[48px] rounded-full">
          <Link to="/find-church" data-testid="welcome-to-finder">Find your church</Link>
        </Button>
      </main>
    );
  }

  const place = [...new Set([church.city, church.region, church.country].filter(Boolean))].join(', ');

  return (
    <main className="min-h-screen bg-stone-50 pb-12" data-testid="church-welcome-page">
      <header
        className="relative overflow-hidden bg-stone-900 px-4 pb-10 pt-10 text-white sm:px-6"
        style={church.hero_image_url ? {
          backgroundImage: `linear-gradient(rgba(12,10,9,0.78), rgba(12,10,9,0.92)), url(${church.hero_image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        } : undefined}
      >
        <div className="mx-auto max-w-2xl">
          {church.logo_url ? (
            <img src={church.logo_url} alt="" className="mb-4 h-14 w-14 rounded-xl bg-white/90 object-contain p-1.5" />
          ) : null}
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400">Welcome to</p>
          <h1 className="mt-2 text-4xl font-bold leading-tight sm:text-5xl" data-testid="welcome-church-name">
            {church.name}
          </h1>
          {place ? (
            <p className="mt-3 flex items-center gap-1.5 text-base text-stone-300">
              <MapPin className="h-4 w-4" />{place}
            </p>
          ) : null}
          <p className="mt-4 max-w-lg text-base text-stone-200">
            {church.welcome_message || 'We would love to welcome you. However you have found us, there is a place for you here.'}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 sm:px-6">
        {standing?.action === 'transfer' ? (
          <section className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="welcome-transfer-offer">
            <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900 md:text-lg">
              <ArrowRightLeft className="h-4 w-4" />You are already an FWM member
            </h2>
            <p className="mt-1 text-sm text-amber-900/80">
              Your membership is with {standing.current_church_name || 'another assembly'}.
              You do not need to apply again — ask {church.name} to receive you, and
              they will be in touch. Nothing changes until they accept.
            </p>
            <Button
              onClick={requestTransfer}
              disabled={requesting}
              className="mt-3 min-h-[48px] rounded-full bg-amber-600 hover:bg-amber-700"
              data-testid="welcome-request-transfer"
            >
              {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Ask to transfer here
            </Button>
          </section>
        ) : null}

        {standing?.action === 'pending' ? (
          <section className="rounded-2xl border border-stone-300 bg-white p-4" data-testid="welcome-transfer-pending">
            <h2 className="flex items-center gap-2 text-base font-semibold text-stone-900">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />Your transfer request is with them
            </h2>
            <p className="mt-1 text-sm text-stone-600">
              {standing.pending_request?.to_church_name || 'The assembly'} will review it
              and be in touch. Your membership stays where it is in the meantime.
            </p>
          </section>
        ) : null}

        {standing?.action === 'already_here' ? (
          <p className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
             data-testid="welcome-already-member">
            You are already a member here. Welcome back.
          </p>
        ) : null}

        <section className="space-y-3">
          <Action
            icon={Info}
            title="Find out more"
            hint="Services, teaching, events and the team"
            to={church.public_path}
            testId="welcome-find-out-more"
          />
          <Action
            icon={CalendarHeart}
            title="Plan a visit"
            hint="Tell us you are coming and we will look out for you"
            onClick={() => setVisitOpen(true)}
            testId="welcome-plan-visit"
          />
          {standing?.action === 'transfer' || standing?.action === 'already_here' ? null : (
            <Action
              icon={UserPlus}
              title="Become a member"
              hint="Start your membership application"
              to={`/church/${church.id}/join`}
              testId="welcome-become-member"
              tone="dark"
            />
          )}
        </section>

        <section className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="welcome-details">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
            Finding us
          </h2>
          <dl className="mt-3 space-y-2.5 text-sm">
            {church.address ? (
              <div className="flex gap-3">
                <dt className="shrink-0 text-stone-400"><MapPin className="h-4 w-4" /></dt>
                <dd className="text-stone-700">{church.address}</dd>
              </div>
            ) : null}
            {church.service_times?.length ? (
              <div className="flex gap-3">
                <dt className="shrink-0 text-stone-400"><Clock className="h-4 w-4" /></dt>
                <dd className="text-stone-700">
                  <ul className="space-y-0.5" data-testid="welcome-service-times">
                    {church.service_times.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </dd>
              </div>
            ) : null}
            {church.contact_phone ? (
              <div className="flex gap-3">
                <dt className="shrink-0 text-stone-400"><Phone className="h-4 w-4" /></dt>
                <dd><a className="text-stone-700 underline" href={`tel:${church.contact_phone}`}>
                  {church.contact_phone}</a></dd>
              </div>
            ) : null}
            {church.contact_email ? (
              <div className="flex gap-3">
                <dt className="shrink-0 text-stone-400"><Mail className="h-4 w-4" /></dt>
                <dd><a className="text-stone-700 underline" href={`mailto:${church.contact_email}`}>
                  {church.contact_email}</a></dd>
              </div>
            ) : null}
            {church.website_url ? (
              <div className="flex gap-3">
                <dt className="shrink-0 text-stone-400"><Globe className="h-4 w-4" /></dt>
                <dd><a className="text-stone-700 underline" href={church.website_url}
                       target="_blank" rel="noopener noreferrer">{church.website_url}</a></dd>
              </div>
            ) : null}
          </dl>
          <Button asChild variant="ghost" size="sm" className="mt-3 min-h-[44px] px-0 text-stone-600">
            <Link to="/find-church" data-testid="welcome-other-churches">
              Looking for a different assembly?
            </Link>
          </Button>
        </section>

        {church.what_to_expect ? (
          <section className="rounded-2xl border border-stone-200 bg-white p-4"
                   data-testid="welcome-what-to-expect">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
              What to expect
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">
              {church.what_to_expect}
            </p>
          </section>
        ) : null}
      </div>

      <PlanVisitDialog
        churchId={church.id}
        churchName={church.name}
        open={visitOpen}
        onOpenChange={setVisitOpen}
      />
    </main>
  );
}
